import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  MessageSquare,
  FileCheck,
  GitBranch,
  Award,
  Search,
  Bot,
  Menu,
  X,
  FileText,
  Sparkles,
} from 'lucide-react';
import Breadcrumbs from './Breadcrumbs';
import { LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AICommandPalette from './AICommandPalette';
import AIActivityFeed from './AIActivityFeed';
import FloatingAIAssistant from './FloatingAIAssistant';
import VoiceCommand from './VoiceCommand';
import AmbientBackground from './AmbientBackground';
import { SyncOfflineData } from './SyncOfflineData';
import logoImg from "../assets/autonomize-black.png"

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  // Get user initials from email or name
  const getUserInitials = () => {
    if (user?.email) {
      const emailParts = user.email.split('@')[0].split('.');
      if (emailParts.length >= 2) {
        return (emailParts[0][0] + emailParts[1][0]).toUpperCase();
      }
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Get user display name
  const getUserName = () => {
    return user?.email?.split('@')[0].replace('.', ' ').toUpperCase() || 'User';
  };

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.innerWidth < 1280;
  });

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
      if (e.key === 'Escape') {
        setCommandPaletteOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleVoiceCommand = (command: string) => {

  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/members', label: 'Members', icon: Users },
    { path: '/care-plans', label: 'Care Plans', icon: ClipboardList },
    { path: '/outreach', label: 'Outreach Center', icon: MessageSquare },
    { path: '/authorizations', label: 'Authorizations', icon: FileCheck },
    { path: '/programs', label: 'Programs & Pathways', icon: GitBranch },
    { path: '/quality', label: 'Quality & Compliance', icon: Award },
    { path: '/assessments/admin', label: 'Assessments', icon: FileText },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Sidebar - Modern Compact Design */}
      <aside
        className={`${sidebarCollapsed ? "w-[60px]" : "w-[200px]"
          } bg-white border-r border-gray-200 transition-all duration-250 ease-in-out flex flex-col`}
        aria-label="Main navigation"
      >
        {/* Header */}
        <div className="h-14 flex items-center justify-between px-3 border-b border-gray-200">
          {!sidebarCollapsed && (
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center">
                {/* <Bot className="w-4 h-4 text-white" /> */}
                {/* <svg width="24" height="24" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="22" r="18" fill="#9333ea"></circle><path d="M32 45 C32 39, 38 35, 50 35 C62 35, 68 39, 68 45 L68 95 L32 95 Z" fill="#9333ea"></path></svg> */}
                <img width={100} src={logoImg} alt="" />
                {/* <img className="brand-logo-img m-b" src="data:image/png;base64,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" alt="Autonomize Logo" /> */}
              </div>
              {/* <div>
                <h1 className="text-[15px] font-semibold text-primary leading-tight">
                  <h2 className="flex flex-col">Care<span className="brand-title-highlight">Management</span></h2>
                </h1>
              </div> */}
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
            aria-label={
              sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
            }
            aria-expanded={!sidebarCollapsed}
          >
            <Menu className="w-5 h-5 text-primary" />
          </button>
        </div>

        {/* Navigation Items */}
        <nav
          className={`flex-1 ${sidebarCollapsed ? "px-2 py-3" : "px-3 py-4"}`}
          role="navigation"
        >
          <div className="space-y-0.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <div key={item.path} className="relative group">
                  <button
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center h-11 rounded-lg transition-all duration-200 relative ${sidebarCollapsed
                      ? "justify-center px-0"
                      : "px-3 space-x-3"
                      } ${active
                        ? "bg-purple-50 text-purple-600"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                      }`}
                    aria-label={item.label}
                    aria-current={active ? "page" : undefined}
                  >
                    {/* Active state left border accent */}
                    {active && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-purple-600 rounded-r-full"></div>
                    )}

                    {/* Icon */}
                    <Icon
                      className={`w-5 h-5 transition-transform duration-200 ${!sidebarCollapsed && !active
                        ? "group-hover:scale-105"
                        : ""
                        } ${sidebarCollapsed ? "flex-shrink-0" : ""}`}
                    />

                    {/* Label (expanded state only) */}
                    {!sidebarCollapsed && (
                      <span className="text-[13px] font-medium truncate">
                        {item.label}
                      </span>
                    )}
                  </button>

                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <div
                      className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2.5 py-1.5 bg-gray-900 bg-opacity-95 text-white text-xs font-medium rounded-md whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 delay-400 z-50"
                      role="tooltip"
                    >
                      {item.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* User Footer */}
        <div
          className={`flex py-4 items-center border-t border-gray-200 ${sidebarCollapsed ? "justify-center px-2" : "px-3"
            }`}
        >
          {!sidebarCollapsed ? (
            <div className="flex flex-col w-full items-start space-y-2.5">
              <div className="flex items-center space-x-2.5 w-full">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-purple-700 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-white">{getUserInitials()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{getUserName()}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
              <div
                onClick={logout}
                className="flex w-full items-center cursor-pointer py-2 gap-2.5 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                title="Logout"
              >
                <LogOut className="w-8  h-4" />
                <span className="hidden sm:inline">Logout</span>
              </div>
            </div>
          ) : (
            <div className="relative group flex flex-col items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-purple-700 rounded-full flex items-center justify-center cursor-pointer">
                <span className="text-xs font-semibold text-white">{getUserInitials()}</span>
              </div>
              <button
                onClick={logout}
                className="flex items-center justify-center p-1.5 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
              {/* Tooltip for user */}
              <div
                className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2.5 py-1.5 bg-gray-900 bg-opacity-95 text-white text-xs font-medium rounded-md whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 delay-400 z-50"
                role="tooltip"
              >
                {getUserName()}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-gradient-to-r from-purple-900 to-purple-700 text-white shadow-lg px-6 py-3">
          {/* <div className="absolute inset-0 bg-gradient-to-r from-purple-50/50 to-purple-100/50 pointer-events-none" /> */}

          <div className="flex items-center justify-between relative z-10">
            {/* <div className="flex-1 max-w-2xl">
              <div className="relative group">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  onClick={() => setCommandPaletteOpen(true)}
                  placeholder="Search or ask AI anything... (Ctrl+K)"
                  className="w-full pl-10 pr-20 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all cursor-pointer hover:border-purple-300"
                  readOnly
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg border border-gray-200 text-xs text-gray-600">
                  <Sparkles className="w-3 h-3 text-purple-600" />
                  <kbd>⌘K</kbd>
                </div>
              </div>
            </div> */}

            <div className="flex items-center gap-3">
              {/* <svg
                width="32"
                height="32"
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="50" cy="22" r="18" fill="#F5A623"></circle>
                <path
                  d="M32 45 C32 39, 38 35, 50 35 C62 35, 68 39, 68 45 L68 95 L32 95 Z"
                  fill="#F5A623"
                ></path>
              </svg> */}

              <div>
                <h1 className="text-2xl font-bold">Agentic Care Management</h1>
                {/* <p className="text-purple-200 text-sm">
                  Phase 1: Member Understanding &amp; Risk Intelligence
                </p> */}
              </div>
            </div>

            <div className="flex items-center gap-3 ml-4">
              <SyncOfflineData />
              <VoiceCommand onCommand={handleVoiceCommand} />

              <button
                onClick={() => setCopilotOpen(!copilotOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${copilotOpen
                  ? "bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg"
                  : "bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 hover:from-purple-100 hover:to-purple-200 border border-purple-200"
                  }`}
              >
                <Bot className="w-5 h-5" />
                <span className="font-medium">AI Copilot</span>
              </button>
            </div>
          </div>
        </header>

        {/* Content Area with Copilot Panel */}
        <div className="flex-1 flex overflow-hidden">
          <main className="flex-1 overflow-auto p-6">
            <Breadcrumbs />
            {children}
          </main>

          {/* Right Copilot Panel */}
          {copilotOpen && (
            <aside className="w-96 bg-white border-l border-gray-200 flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center">
                  <Bot className="w-6 h-6 text-purple-600 mr-2" />
                  <h2 className="font-semibold text-gray-900">AI Copilot</h2>
                </div>
                <button
                  onClick={() => setCopilotOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="flex-1 overflow-auto p-4">
                <div className="space-y-4">
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-start">
                      <Bot className="w-5 h-5 text-purple-600 mr-2 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-700 mb-2">
                          Hi! I'm your AI Care Copilot. I can help you with:
                        </p>
                        <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                          <li>Analyzing member risk factors</li>
                          <li>Suggesting care interventions</li>
                          <li>Drafting communications</li>
                          <li>Reviewing authorizations</li>
                          <li>Generating care plans</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <button className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
                      <p className="text-sm font-medium text-gray-900">
                        Analyze high-risk members
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Review members with risk score &gt; 80
                      </p>
                    </button>

                    <button className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
                      <p className="text-sm font-medium text-gray-900">
                        Draft outreach message
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Create personalized communication
                      </p>
                    </button>

                    <button className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
                      <p className="text-sm font-medium text-gray-900">
                        Review pending authorizations
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Analyze urgent authorization requests
                      </p>
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    placeholder="Ask AI Copilot..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium">
                    Send
                  </button>
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>

      <AmbientBackground />
      <AICommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
      />
      <AIActivityFeed />
      <FloatingAIAssistant />
    </div>
  );
}
